import { buildExpandCollapseViewConfig } from '@components/FilterV3/components/ExpandCollapseButtonGroup/expandCollapseButtonGroup.utils';
import useWidth from '@components/Hooks/useWidth';
import { useObjectContext } from '@components/v2/layout/ObjectScreen/hooks/useObjectContext';
import { useState } from 'react';
import LangEnum from '../../../../../../../lang/LangEnum';
import {
  useOrderTargets,
  useTargetDetailsByTargetIds,
} from '../../../../../../../services/modules/target/target.queries';
import { OrderObjectTargetsTestIds } from '../../../../../../../utils/testIds/OrderObject.testIds';
import { UseOrderObjectTargetsTabParams } from '../orderObjectTargetsTab.types';
import {
  buildOrderTargetRows,
  getExpandableIds,
  getOrderTargetUnits,
} from '../orderObjectTargetsTab.utils';
import useOrderTargetsBreakdowns from './useOrderTargetsBreakdowns';
import useOrderTargetsColumns from './useOrderTargetsColumns';
import useOrderTargetsExpandedIds from './useOrderTargetsExpandedIds';

const useOrderObjectTargetsTab = ({ orderId }: UseOrderObjectTargetsTabParams) => {
  const { data, isLoading, isError, refetch } = useOrderTargets(orderId);
  const targets = data ?? [];
  const targetIds = targets.map((target) => target.id);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const details = useTargetDetailsByTargetIds(targetIds, revealedIds);
  const { expandedIds, isAllExpanded, isUsingFullTrees, itemTargetIds, changeExpansion, toggle } =
    useOrderTargetsExpandedIds();
  const {
    breakdowns,
    hasError: hasBreakdownError,
    refetch: refetchBreakdowns,
  } = useOrderTargetsBreakdowns({ targetIds, expandedIds, itemTargetIds, isUsingFullTrees });
  const { scrollParent } = useObjectContext();
  const { smallWidth } = useWidth();

  const visibleExpandedIds = isAllExpanded
    ? getExpandableIds({ targets, breakdowns })
    : expandedIds;

  const rows = buildOrderTargetRows({
    targets,
    breakdowns,
    details,
    expandedIds: visibleExpandedIds,
  });

  const targetIdByRowId = new Map(rows.map((row) => [row.id, row.targetId]));

  const revealSummary = (targetId: string) =>
    setRevealedIds((current) => (current.has(targetId) ? current : new Set(current).add(targetId)));

  const columns = useOrderTargetsColumns({
    units: getOrderTargetUnits(targets),
    isCompact: smallWidth,
    onToggle: (rowId) =>
      toggle({ rowId, targetId: targetIdByRowId.get(rowId) ?? rowId, visibleExpandedIds }),
    onRevealSummary: revealSummary,
  });

  const hasListError = isError && !data;

  const reload = () => {
    refetch();
    refetchBreakdowns();
    details.forEach((entry) => entry.refetch());
  };

  const viewConfigs = [
    buildExpandCollapseViewConfig({
      label: LangEnum.GLOBAL_PLANS,
      isExpanded: isAllExpanded || expandedIds.size > 0,
      defaultValue: false,
      collapseTestId: OrderObjectTargetsTestIds.COLLAPSE_ALL_BUTTON,
      expandTestId: OrderObjectTargetsTestIds.EXPAND_ALL_BUTTON,
      onApply: changeExpansion,
    }),
  ];

  return {
    rows,
    columns,
    viewConfigs,
    scrollParent,
    isLoading: isLoading && !data,
    isError: hasListError || hasBreakdownError,
    errorTitle: hasListError
      ? LangEnum.ORDER_OBJECT_TARGETS_ERROR
      : LangEnum.ORDER_OBJECT_TARGETS_BREAKDOWNS_ERROR,
    isEmpty: !!data && data.length === 0,
    refetch: reload,
  };
};

export default useOrderObjectTargetsTab;
